import { useCallback, useEffect, useRef, useState } from "react";
import { marked } from "marked";
import TurndownService from "turndown";

const turndown = new TurndownService({ headingStyle: "atx", bulletListMarker: "-" });
import { useRouter, useParams } from "../lib/router";
import { useAuth } from "../lib/auth-context";
import { DictationEditor, type EditorHandle } from "../components/dictation-editor";
import { MicButton } from "../components/mic-button";
import { AudioUpload } from "../components/audio-upload";
import { ModelLoader } from "../components/model-loader";
import { SOPPreview } from "../components/sop-preview";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { saveDraft, getDraft, saveVersion, getVersions, type DraftStatus, type DraftType, type DraftVersion } from "../lib/drafts";
import { VersionHistory } from "../components/version-history";
import { applyWordReplacements, type WordReplacementMap } from "../lib/word-replacement";
import { loadReplacements } from "../lib/replacement-store";
import { sendMessage, getRoomName, findOrCreateVoxDocRoom, uploadFile, sendFileMessage, type RoomInfo } from "../lib/matrix-client";
import { useOnline } from "../lib/use-online";
import { useTranscriber } from "../lib/use-transcriber";

export default function DictatePage() {
  const { navigate } = useRouter();
  const { id: draftId } = useParams();
  const { auth, isLoading: authLoading } = useAuth();
  const isOnline = useOnline();
  const editorRef = useRef<EditorHandle>(null);
  const transcriber = useTranscriber();

  const [title, setTitle] = useState("");
  const [editorText, setEditorText] = useState("");
  const [editorHtml, setEditorHtml] = useState("");
  const [initialContent, setInitialContent] = useState("");

  // Transcription state
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [lastTranscribeTime, setLastTranscribeTime] = useState("");
  const [replacementCount, setReplacementCount] = useState(0);
  const replacementsRef = useRef<WordReplacementMap>({});

  // SOP structuring state
  const [isStructuring, setIsStructuring] = useState(false);
  const [structuredSOP, setStructuredSOP] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishStatus, setPublishStatus] = useState("");
  const [targetRoom, setTargetRoom] = useState<RoomInfo | null>(null);
  const [loadingTarget, setLoadingTarget] = useState(false);
  const [targetError, setTargetError] = useState<string | null>(null);

  // Draft lifecycle state
  const [draftStatus, setDraftStatus] = useState<DraftStatus>("draft");
  // Derive default doc type from id prefix (blank-* = blank, anything else = sop)
  const [docType, setDocType] = useState<DraftType>(draftId.startsWith("blank-") ? "blank" : "sop");
  const [rawDictation, setRawDictation] = useState("");
  const [markdownSource, setMarkdownSource] = useState("");
  const [copied, setCopied] = useState(false);
  const [saveConfirm, setSaveConfirm] = useState(false);
  const [versions, setVersions] = useState<DraftVersion[]>([]);

  // Auto-save debounce
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refs for latest state (used by unmount save to avoid stale closures)
  const latestRef = useRef({ title, editorHtml, editorText, draftStatus, rawDictation, markdownSource, draftId, docType });
  latestRef.current = { title, editorHtml, editorText, draftStatus, rawDictation, markdownSource, draftId, docType };

  // Flush pending save on unmount (navigating away)
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      const s = latestRef.current;
      const content = s.editorHtml || s.editorText;
      if (content) {
        saveDraft({
          id: s.draftId,
          title: s.title || (s.docType === "blank" ? "Untitled Document" : "Untitled SOP"),
          content,
          updatedAt: Date.now(),
          status: s.draftStatus,
          rawDictation: s.rawDictation || undefined,
          markdownSource: s.markdownSource || undefined,
          type: s.docType,
        });
      }
    };
  }, []);

  // Load word replacements
  useEffect(() => {
    loadReplacements().then(({ map }) => {
      replacementsRef.current = map;
    });
  }, []);

  // Load existing draft and versions
  useEffect(() => {
    getDraft(draftId).then((draft) => {
      if (draft) {
        setTitle(draft.title);
        setEditorHtml(draft.content);
        if (draft.status) setDraftStatus(draft.status);
        if (draft.type) setDocType(draft.type);
        if (draft.rawDictation) setRawDictation(draft.rawDictation);
        if (draft.markdownSource) {
          setMarkdownSource(draft.markdownSource);
          setStructuredSOP(draft.markdownSource);
          const html = marked.parse(draft.markdownSource) as string;
          setInitialContent(html);
        } else {
          setInitialContent(draft.content);
        }
      }
    });
    getVersions(draftId).then(setVersions);
  }, [draftId]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !auth) {
      navigate("/login");
    }
  }, [auth, authLoading, navigate]);

  // Wire up transcriber results
  useEffect(() => {
    transcriber.onResult((text: string, time: string) => {
      const { text: replaced, count } = applyWordReplacements(
        text,
        replacementsRef.current
      );
      setReplacementCount(count);
      editorRef.current?.appendText(replaced);
      setIsTranscribing(false);
      setLastTranscribeTime(time);
    });
  }, [transcriber]);

  const ensureModelLoaded = useCallback(() => {
    transcriber.loadModel();
  }, [transcriber]);

  // Auto-save with 2s debounce — saves HTML for formatting preservation
  const handleEditorUpdate = useCallback(
    (html: string, text: string) => {
      setEditorHtml(html);
      setEditorText(text);
      // Sync ref immediately so unmount flush always has latest content
      latestRef.current.editorHtml = html;
      latestRef.current.editorText = text;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        saveDraft({
          id: draftId,
          title: title || (docType === "blank" ? "Untitled Document" : "Untitled SOP"),
          content: html,
          updatedAt: Date.now(),
          status: draftStatus,
          rawDictation: rawDictation || undefined,
          markdownSource: markdownSource || undefined,
          type: docType,
        });
      }, 2000);
    },
    [draftId, title, draftStatus, rawDictation, markdownSource, docType]
  );

  // Save title changes
  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
    saveDraft({
      id: draftId,
      title: newTitle || (docType === "blank" ? "Untitled Document" : "Untitled SOP"),
      content: editorHtml || editorText,
      updatedAt: Date.now(),
      status: draftStatus,
      rawDictation: rawDictation || undefined,
      markdownSource: markdownSource || undefined,
      type: docType,
    });
  };

  const handleAudioReady = useCallback((audioData: Float32Array) => {
    setIsTranscribing(true);
    transcriber.transcribe(audioData);
  }, [transcriber]);

  const refreshVersions = async () => {
    const v = await getVersions(draftId);
    setVersions(v);
  };

  const loadRooms = () => {
    if (!auth || loadingTarget || targetRoom) return;
    setLoadingTarget(true);
    setTargetError(null);
    findOrCreateVoxDocRoom(auth)
      .then((room) => setTargetRoom(room))
      .catch((err) => {
        console.error("[VoxDoc] failed to resolve target room:", err);
        setTargetError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setLoadingTarget(false));
  };

  const handleStructure = async () => {
    if (!editorText.trim()) return;
    setIsStructuring(true);
    // Save version snapshot and raw dictation before structuring
    setRawDictation(editorText);
    await saveVersion(draftId, editorText, "Before AI structuring", draftStatus, markdownSource || undefined);
    refreshVersions();
    try {
      const res = await fetch("/api/structure", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: editorText, author: auth?.userId?.replace(/^@/, "").replace(/:.*$/, "") }),
      });
      if (!res.ok) throw new Error("Structuring failed");
      const data = await res.json();
      setStructuredSOP(data.markdown);
      setMarkdownSource(data.markdown);
      setShowPreview(true);
      loadRooms();
    } catch (err) {
      console.error(err);
    } finally {
      setIsStructuring(false);
    }
  };

  const applyStructuredToEditor = (markdown: string) => {
    const html = marked.parse(markdown) as string;
    editorRef.current?.replaceContent(html);
    setEditorText(markdown);
    setEditorHtml(html);
    setDraftStatus("structured");
    saveDraft({
      id: draftId,
      title: title || "Untitled SOP",
      content: html,
      updatedAt: Date.now(),
      status: "structured",
      rawDictation: rawDictation || undefined,
      markdownSource: markdown,
      type: docType,
    });
  };

  const handleUseStructured = () => {
    // For blank docs, the editor already holds the user's content — don't overwrite.
    // applyStructuredToEditor sets status to "structured", which is wrong for blank docs.
    if (docType !== "blank" && structuredSOP) {
      applyStructuredToEditor(structuredSOP);
    }
    setShowPreview(false);
  };

  const handleRevertToRaw = () => {
    if (!rawDictation) return;
    editorRef.current?.replaceContent(rawDictation);
    setEditorText(rawDictation);
    setEditorHtml(rawDictation);
    setMarkdownSource("");
    setStructuredSOP("");
    setDraftStatus("draft");
    saveDraft({
      id: draftId,
      title: title || "Untitled SOP",
      content: rawDictation,
      updatedAt: Date.now(),
      status: "draft",
      type: docType,
    });
  };

  const handleCopyMarkdown = async () => {
    const textToCopy = markdownSource || editorText;
    await navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // For blank documents: convert editor HTML to markdown so the preview/publish
  // path can reuse the same SOPPreview modal and Matrix-publish flow.
  const handleSaveBlankToMatrix = () => {
    const html = editorRef.current?.getHTML() || editorHtml;
    const text = editorRef.current?.getText() || editorText;
    if (!html.trim() && !text.trim()) return;
    const md = html.trim() ? turndown.turndown(html) : text;
    setStructuredSOP(md);
    setMarkdownSource(md);
    setShowPreview(true);
    loadRooms();
  };

  const handlePublish = async (roomId: string) => {
    if (!auth || !structuredSOP || !roomId) return;
    setIsPublishing(true);
    await saveVersion(draftId, editorText, "Before publishing", draftStatus, markdownSource || undefined);
    refreshVersions();
    try {
      const roomName = await getRoomName(auth, roomId);
      const fallbackTitle = docType === "blank" ? "Untitled Document" : "Untitled SOP";
      const sopTitle = title || fallbackTitle;
      const filename = `${sopTitle.replace(/[^a-zA-Z0-9-_ ]/g, "").replace(/\s+/g, "-")}.md`;

      // Header emoji differs per type so Matrix readers can tell at a glance
      const headerEmoji = docType === "blank" ? "\u{1F4DD}" : "\u{1F4CB}"; // memo vs clipboard
      const header = `${headerEmoji} **${sopTitle}**\n\n`;
      await sendMessage(auth, roomId, header + structuredSOP);

      // Also upload and attach as .md file
      try {
        const blob = new Blob([structuredSOP], { type: "text/markdown" });
        const mxcUri = await uploadFile(auth, blob, filename, "text/markdown");
        await sendFileMessage(auth, roomId, mxcUri, filename, blob.size, "text/markdown");
      } catch (fileErr) {
        console.warn("File attachment failed (text was still sent):", fileErr);
      }

      // For SOPs, load the AI-structured version back into the editor.
      // For blank docs, the editor already has the user's content — don't overwrite it.
      if (docType !== "blank") {
        applyStructuredToEditor(structuredSOP);
      }
      setDraftStatus("published");
      saveDraft({
        id: draftId,
        title: sopTitle,
        content: docType === "blank" ? (editorRef.current?.getHTML() || editorHtml) : structuredSOP,
        updatedAt: Date.now(),
        status: "published",
        rawDictation: rawDictation || undefined,
        markdownSource: structuredSOP,
        type: docType,
      });
      setShowPreview(false);
      setPublishStatus(`Published to: ${roomName} (${filename})`);
    } catch (err) {
      console.error(err);
      setPublishStatus(
        `Publish failed: ${err instanceof Error ? err.message : "Unknown error"}`
      );
    } finally {
      setIsPublishing(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  const statusColors: Record<DraftStatus, string> = {
    draft: "bg-yellow-500/10 text-yellow-500 border-yellow-500/20",
    structured: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    published: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  };

  return (
    <div className="min-h-screen p-3 sm:p-6 max-w-3xl mx-auto">
      <header className="flex items-center justify-between mb-6">
        <Button variant="ghost" onClick={() => {
          // Immediate save before navigating
          if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
          const s = latestRef.current;
          const content = s.editorHtml || s.editorText;
          if (content) {
            saveDraft({
              id: s.draftId,
              title: s.title || (s.docType === "blank" ? "Untitled Document" : "Untitled SOP"),
              content,
              updatedAt: Date.now(),
              status: s.draftStatus,
              rawDictation: s.rawDictation || undefined,
              markdownSource: s.markdownSource || undefined,
              type: s.docType,
            });
          }
          navigate("/");
        }}>
          &larr; Back
        </Button>
        <div className="flex items-center gap-2">
          <Badge className={statusColors[draftStatus]}>
            {draftStatus.charAt(0).toUpperCase() + draftStatus.slice(1)}
          </Badge>
          {lastTranscribeTime && (
            <Badge variant="secondary">{lastTranscribeTime}s</Badge>
          )}
          {replacementCount > 0 && (
            <Badge variant="outline" className="text-emerald-500">
              {replacementCount} replacement{replacementCount > 1 ? "s" : ""}
            </Badge>
          )}
        </div>
      </header>

      <Input
        value={title}
        onChange={(e) => handleTitleChange(e.target.value)}
        placeholder={docType === "blank" ? "Document Title" : "SOP Title"}
        className="text-xl font-semibold mb-4 border-none bg-transparent p-0 focus-visible:ring-0"
      />

      <ModelLoader
        status={transcriber.modelStatus}
        progress={transcriber.loadProgress}
        error={transcriber.error || ""}
        isIOS={transcriber.isIOS}
      />

      <div className="my-4 flex flex-wrap items-center gap-4">
        {transcriber.modelStatus === "idle" ? (
          <Button
            size="lg"
            onClick={ensureModelLoaded}
            className="bg-red-600 hover:bg-red-700 text-white h-14 px-8 text-base sm:h-10 sm:px-6 sm:text-sm"
          >
            Enable Dictation
          </Button>
        ) : (
          <MicButton
            onAudioReady={handleAudioReady}
            disabled={transcriber.modelStatus !== "ready" || isTranscribing}
          />
        )}
        <AudioUpload
          onAudioReady={handleAudioReady}
          disabled={transcriber.modelStatus !== "ready"}
        />
        {isTranscribing && (
          <span className="text-sm text-purple-400 animate-pulse">
            Transcribing...
          </span>
        )}
      </div>

      <DictationEditor
        ref={editorRef}
        content={initialContent}
        onUpdate={handleEditorUpdate}
      />

      <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
        {docType !== "blank" && (
          <Button
            onClick={handleStructure}
            disabled={!editorText.trim() || isStructuring || !isOnline}
          >
            {!isOnline ? "Offline" : isStructuring ? "Structuring..." : draftStatus === "structured" ? "Re-structure with AI" : "Structure with AI"}
          </Button>
        )}

        {docType !== "blank" && rawDictation && draftStatus !== "draft" && (
          <Button variant="outline" onClick={handleRevertToRaw}>
            Revert to Raw
          </Button>
        )}

        <Button variant="outline" onClick={handleCopyMarkdown}>
          {copied ? "Copied!" : "Copy Markdown"}
        </Button>

        {(editorText.trim() || editorHtml.trim()) && (
          <Button variant="outline" onClick={async () => {
            const content = editorRef.current?.getText() || editorText;
            await saveVersion(draftId, content, "Manual save", draftStatus, markdownSource || undefined);
            await refreshVersions();
            setSaveConfirm(true);
            setTimeout(() => setSaveConfirm(false), 2000);
          }}>
            {saveConfirm ? "Saved!" : "Save Version"}
          </Button>
        )}

        {docType !== "blank" && structuredSOP && !showPreview && draftStatus !== "draft" && (
          <Button variant="outline" onClick={() => { setShowPreview(true); loadRooms(); }}>
            Preview & Publish
          </Button>
        )}

        {docType === "blank" && (editorText.trim() || editorHtml.trim()) && (
          <Button onClick={handleSaveBlankToMatrix} disabled={!isOnline}>
            {!isOnline ? "Offline" : "Save to Matrix"}
          </Button>
        )}
      </div>

      <VersionHistory
        versions={versions}
        onRestore={(v) => {
          if (v.markdownSource) {
            const html = marked.parse(v.markdownSource) as string;
            editorRef.current?.replaceContent(html);
            setEditorText(v.content);
            setEditorHtml(html);
            setMarkdownSource(v.markdownSource);
            setStructuredSOP(v.markdownSource);
          } else {
            editorRef.current?.replaceContent(v.content);
            setEditorText(v.content);
            setEditorHtml(v.content);
            setMarkdownSource("");
            setStructuredSOP("");
          }
          setDraftStatus(v.status);
          saveDraft({
            id: draftId,
            title: title || "Untitled SOP",
            content: v.markdownSource ? marked.parse(v.markdownSource) as string : v.content,
            updatedAt: Date.now(),
            status: v.status,
            rawDictation: rawDictation || undefined,
            markdownSource: v.markdownSource || undefined,
            type: docType,
          });
        }}
      />

      {publishStatus && (
        <div className={`mt-4 p-3 rounded-lg text-sm ${
          publishStatus.startsWith("Published")
            ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
            : "bg-destructive/10 text-destructive border border-destructive/20"
        }`}>
          {publishStatus}
        </div>
      )}

      <SOPPreview
        open={showPreview}
        onClose={handleUseStructured}
        markdown={structuredSOP}
        onPublish={handlePublish}
        publishing={isPublishing}
        targetRoom={targetRoom}
        loadingTarget={loadingTarget}
        targetError={targetError}
        title={docType === "blank" ? "Document Preview" : "Structured SOP Preview"}
      />
    </div>
  );
}
